import { pb } from "@/lib/pocketbase"
import { Posts, PostsStatusOptions } from "@/types/pocketbase-types"
import { format, addDays, startOfDay, endOfDay } from "date-fns"

export interface ScheduleConflict {
  date: Date
  posts: Posts[]
}

export interface ScheduleResult {
  success: boolean
  suggestedDate?: Date
  conflicts?: ScheduleConflict[]
  error?: string
}

export class SchedulingService {
  /**
   * Check if a date has scheduling conflicts for a specific brand
   */
  static async checkDateConflicts(
    brandId: string, 
    targetDate: Date
  ): Promise<ScheduleConflict | null> {
    try {
      const startDate = startOfDay(targetDate)
      const endDate = endOfDay(targetDate)
      
      const existingPosts = await pb.collection("posts").getList<Posts>(1, 50, {
        filter: `brand = "${brandId}" && status = "scheduled" && scheduleDate >= "${startDate.toISOString()}" && scheduleDate <= "${endDate.toISOString()}"`,
        sort: "scheduleDate",
      })

      if (existingPosts.items.length > 0) {
        return {
          date: targetDate,
          posts: existingPosts.items,
        }
      }

      return null
    } catch (error) {
      console.error("Error checking date conflicts:", error)
      return null
    }
  }

  /**
   * Find the next available date for scheduling (no conflicts)
   */
  static async findNextAvailableDate(
    brandId: string, 
    startDate: Date, 
    maxDaysToCheck: number = 30
  ): Promise<Date> {
    let currentDate = new Date(startDate)
    
    for (let i = 0; i < maxDaysToCheck; i++) {
      const conflict = await this.checkDateConflicts(brandId, currentDate)
      if (!conflict) {
        return currentDate
      }
      currentDate = addDays(currentDate, 1)
    }
    
    // If no available date found, return the original date + maxDaysToCheck
    return addDays(startDate, maxDaysToCheck)
  }

  /**
   * Schedule a post for a specific date
   */
  static async schedulePost(
    postId: string,
    scheduleDate: Date
  ): Promise<ScheduleResult> {
    try {
      await pb.collection("posts").update(postId, {
        status: PostsStatusOptions.Scheduled,
        scheduleDate: scheduleDate.toISOString(),
      })

      return {
        success: true,
      }
    } catch (error) {
      console.error("Error scheduling post:", error)
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to schedule post",
      }
    }
  }

  /**
   * Reschedule existing posts to resolve conflicts
   */
  static async resolveConflicts(
    brandId: string,
    targetDate: Date,
    conflictedPosts: Posts[]
  ): Promise<ScheduleResult> {
    try {
      // Find next available dates for each conflicted post
      let nextAvailableDate = addDays(targetDate, 1)
      
      for (const post of conflictedPosts) {
        // Find next available date for this post
        nextAvailableDate = await this.findNextAvailableDate(brandId, nextAvailableDate)
        
        // Reschedule the post
        await pb.collection("posts").update(post.id, {
          scheduleDate: nextAvailableDate.toISOString(),
        })
        
        // Move to next day for the next post
        nextAvailableDate = addDays(nextAvailableDate, 1)
      }

      return {
        success: true,
        suggestedDate: targetDate,
      }
    } catch (error) {
      console.error("Error resolving conflicts:", error)
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to resolve conflicts",
      }
    }
  }

  /**
   * Schedule post with automatic conflict resolution
   */
  static async scheduleWithConflictResolution(
    postId: string,
    brandId: string,
    targetDate: Date,
    autoResolveConflicts: boolean = false
  ): Promise<ScheduleResult> {
    // Check for conflicts first
    const conflict = await this.checkDateConflicts(brandId, targetDate)
    
    if (conflict && !autoResolveConflicts) {
      // Return conflict info for user to decide
      const suggestedDate = await this.findNextAvailableDate(brandId, addDays(targetDate, 1))
      
      return {
        success: false,
        suggestedDate,
        conflicts: [conflict],
        error: `${conflict.posts.length} post(s) already scheduled for ${format(targetDate, "PPP")}`,
      }
    }
    
    if (conflict && autoResolveConflicts) {
      // Automatically resolve conflicts
      const resolveResult = await this.resolveConflicts(brandId, targetDate, conflict.posts)
      if (!resolveResult.success) {
        return resolveResult
      }
    }
    
    // Schedule the post
    return await this.schedulePost(postId, targetDate)
  }

  /**
   * Unschedule a post (set back to draft)
   */
  static async unschedulePost(postId: string): Promise<ScheduleResult> {
    try {
      await pb.collection("posts").update(postId, {
        status: PostsStatusOptions.Draft,
        scheduleDate: null,
      })

      return {
        success: true,
      }
    } catch (error) {
      console.error("Error unscheduling post:", error)
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to unschedule post",
      }
    }
  }
}